import { Search, X } from 'lucide-react';
import type { CSSProperties, KeyboardEvent, RefObject } from 'react';
import type {
  LiksDropdownOption,
  LiksDropdownVariant,
} from './LiksDropdown.types';
import { LiksDropdownOptionRow } from './LiksDropdownOptionRow';

export type LiksDropdownPanelProps = {
  panelRef: RefObject<HTMLDivElement | null>;
  optionsListRef: RefObject<HTMLDivElement | null>;
  filterInputRef: RefObject<HTMLInputElement | null>;
  optionRefs: RefObject<Array<HTMLDivElement | null>>;
  panelStyle: CSSProperties;
  variant: LiksDropdownVariant;
  filterable: boolean;
  filterId: string;
  listboxId: string;
  ariaLabel: string;
  filterQuery: string;
  filterPlaceholder?: string;
  filterAriaLabel?: string;
  filterClearLabel?: string;
  filterEmptyLabel?: string;
  activeOptionId: string | undefined;
  activeIndex: number;
  value: string;
  visibleOptions: LiksDropdownOption[];
  onFilterChange: (nextQuery: string) => void;
  onFilterKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onClearFilter: () => void;
  onActiveIndexChange: (index: number) => void;
  onSelectIndex: (index: number) => void;
};

export function LiksDropdownPanel({
  panelRef,
  optionsListRef,
  filterInputRef,
  optionRefs,
  panelStyle,
  variant,
  filterable,
  filterId,
  listboxId,
  ariaLabel,
  filterQuery,
  filterPlaceholder,
  filterAriaLabel,
  filterClearLabel,
  filterEmptyLabel,
  activeOptionId,
  activeIndex,
  value,
  visibleOptions,
  onFilterChange,
  onFilterKeyDown,
  onClearFilter,
  onActiveIndexChange,
  onSelectIndex,
}: LiksDropdownPanelProps) {
  const optionsContent =
    visibleOptions.length === 0 ? (
      filterEmptyLabel ? (
        <div className="liks-dropdown__empty" role="status">
          {filterEmptyLabel}
        </div>
      ) : null
    ) : (
      visibleOptions.map((option, index) => {
        const isSelected = option.value === value;
        const isActive = index === activeIndex;
        const optionId = `${listboxId}-option-${index}`;

        return (
          <LiksDropdownOptionRow
            key={option.value}
            option={option}
            optionId={optionId}
            isSelected={isSelected}
            isActive={isActive}
            onMouseEnter={() => onActiveIndexChange(index)}
            onClick={() => onSelectIndex(index)}
            optionRef={(element) => {
              optionRefs.current[index] = element;
            }}
          />
        );
      })
    );

  return (
    <div
      ref={panelRef}
      className={[
        'liks-dropdown__panel',
        `liks-dropdown__panel--${variant}`,
        filterable && 'liks-dropdown__panel--filterable',
      ]
        .filter(Boolean)
        .join(' ')}
      style={panelStyle}
    >
      {filterable ? (
        <>
          <div className="liks-dropdown__filter">
            <span className="liks-dropdown__filter-icon" aria-hidden>
              <Search strokeWidth={2} />
            </span>
            <input
              ref={filterInputRef}
              id={filterId}
              type="text"
              className="liks-dropdown__filter-input"
              value={filterQuery}
              placeholder={filterPlaceholder}
              aria-label={filterAriaLabel ?? filterPlaceholder}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={activeOptionId}
              onChange={(event) => {
                onFilterChange(event.target.value);
              }}
              onKeyDown={onFilterKeyDown}
            />
            {filterQuery.length > 0 && filterClearLabel ? (
              <button
                type="button"
                className="liks-dropdown__filter-clear"
                aria-label={filterClearLabel}
                onMouseDown={(event) => {
                  // Keep focus/open state; avoid trigger blur closing races.
                  event.preventDefault();
                }}
                onClick={onClearFilter}
              >
                <X
                  className="liks-dropdown__filter-clear-icon"
                  strokeWidth={3}
                  aria-hidden
                />
              </button>
            ) : null}
          </div>
          <div
            ref={optionsListRef}
            id={listboxId}
            className="liks-dropdown__options liks-dropdown__options--filterable"
            role="listbox"
            aria-label={ariaLabel}
          >
            {optionsContent}
          </div>
        </>
      ) : (
        <div
          ref={optionsListRef}
          id={listboxId}
          className="liks-dropdown__options liks-dropdown__options--unfiltered"
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
        >
          {optionsContent}
        </div>
      )}
    </div>
  );
}
